"use es6";

import { Observable } from './Observable';
import { Subscriber } from './Subscriber';
import { Subscription } from './Subscription';
import { ObjectUnsubscribedError } from './util/ObjectUnsubscribedError';
import { SubjectSubscription } from './SubjectSubscription';
import { rxSubscriber as rxSubscriberSymbol } from './symbol/rxSubscriber';
/**
 * @class SubjectSubscriber<T>
 */

export class SubjectSubscriber extends Subscriber {
  constructor(destination) {
    super(destination);
    this.destination = destination;
  }

}
/**
 * @class Subject<T>
 */

export class Subject extends Observable {
  constructor() {
    super();
    this.observers = [];
    this.closed = false;
    this.isStopped = false;
    this.hasError = false;
    this.thrownError = null;
  }

  [rxSubscriberSymbol]() {
    return new SubjectSubscriber(this);
  }

  lift(operator) {
    const subject = new AnonymousSubject(this, this);
    subject.operator = operator;
    return subject;
  }

  next(value) {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    }

    if (!this.isStopped) {
      const {
        observers
      } = this;
      const len = observers.length;
      const copy = observers.slice();

      for (let i = 0; i < len; i++) {
        copy[i].next(value);
      }
    }
  }

  error(err) {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    }

    this.hasError = true;
    this.thrownError = err;
    this.isStopped = true;
    const {
      observers
    } = this;
    const len = observers.length;
    const copy = observers.slice();

    for (let i = 0; i < len; i++) {
      copy[i].error(err);
    }

    this.observers.length = 0;
  }

  complete() {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    }

    this.isStopped = true;
    const {
      observers
    } = this;
    const len = observers.length;
    const copy = observers.slice();

    for (let i = 0; i < len; i++) {
      copy[i].complete();
    }

    this.observers.length = 0;
  }

  unsubscribe() {
    this.isStopped = true;
    this.closed = true;
    this.observers = null;
  }

  _trySubscribe(subscriber) {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    } else {
      return super._trySubscribe(subscriber);
    }
  }
  /** @deprecated internal use only */


  _subscribe(subscriber) {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    } else if (this.hasError) {
      subscriber.error(this.thrownError);
      return Subscription.EMPTY;
    } else if (this.isStopped) {
      subscriber.complete();
      return Subscription.EMPTY;
    } else {
      this.observers.push(subscriber);
      return new SubjectSubscription(this, subscriber);
    }
  }

  asObservable() {
    const observable = new Observable();
    observable.source = this;
    return observable;
  }

}

Subject.create = (destination, source) => {
  return new AnonymousSubject(destination, source);
};
/**
 * @class AnonymousSubject<T>
 */


export class AnonymousSubject extends Subject {
  constructor(destination, source) {
    super();
    this.destination = destination;
    this.source = source;
  }

  next(value) {
    const {
      destination
    } = this;

    if (destination && destination.next) {
      destination.next(value);
    }
  }

  error(err) {
    const {
      destination
    } = this;

    if (destination && destination.error) {
      this.destination.error(err);
    }
  }

  complete() {
    const {
      destination
    } = this;

    if (destination && destination.complete) {
      this.destination.complete();
    }
  }
  /** @deprecated internal use only */


  _subscribe(subscriber) {
    const {
      source
    } = this;

    if (source) {
      return this.source.subscribe(subscriber);
    } else {
      return Subscription.EMPTY;
    }
  }

}